import { ArrowDown, Check, Copy } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Markdown, { type Components } from "react-markdown";
import { Button } from "@/components/ui/button";
import type { StoredMessage } from "@/lib/api/conversations";
import { cn } from "@/lib/utils/cn";

/** Distance from the bottom (px) that still counts as "following" the conversation. */
const FOLLOW_THRESHOLD = 80;

// Replies are Markdown; these keep it readable without a typography plugin. Images show their
// alt text only: a remote image loads without a click, so text injected into the model's input
// could make it put data into an image URL and send it out.
const MARKDOWN: Components = {
  img: ({ alt }) => <>{alt}</>,
  p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>,
  h1: ({ children }) => <h3 className="mt-4 mb-2 text-base font-semibold">{children}</h3>,
  h2: ({ children }) => <h3 className="mt-4 mb-2 text-base font-semibold">{children}</h3>,
  h3: ({ children }) => <h3 className="mt-3 mb-1.5 font-semibold">{children}</h3>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noreferrer" className="underline underline-offset-4">
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto rounded-lg bg-muted p-3 text-xs [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 pl-3 text-muted-foreground">{children}</blockquote>
  ),
};

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 text-muted-foreground"
      aria-label={copied ? t("assistant.copied") : t("assistant.copy")}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check /> : <Copy />}
    </Button>
  );
}

/**
 * The conversation. Follows new text to the bottom while you are at the bottom; scrolling up stops
 * that and shows a button back down.
 */
export function ChatMessages({
  messages,
  streaming,
  empty,
}: {
  messages: StoredMessage[];
  /** A reply is being written (the last message grows). */
  streaming: boolean;
  /** Shown when there are no messages yet. */
  empty: ReactNode;
}) {
  const { t } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const [atBottom, setAtBottom] = useState(true);

  const toBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const element = scroller.current;
    if (element) element.scrollTo({ top: element.scrollHeight, behavior });
  }, []);

  // New messages or streamed text: stay at the bottom if the reader is there.
  useLayoutEffect(() => {
    if (following.current) toBottom();
  }, [messages, toBottom]);

  // Sending a message always jumps down.
  const count = messages.filter((message) => message.role === "user").length;
  useEffect(() => {
    following.current = true;
    toBottom("smooth");
  }, [count, toBottom]);

  const last = messages[messages.length - 1];
  const waiting = streaming && (!last || last.role === "user" || !last.content);

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scroller}
        className="h-full overflow-y-auto overscroll-contain"
        onScroll={(event) => {
          const element = event.currentTarget;
          const bottom =
            element.scrollHeight - element.scrollTop - element.clientHeight < FOLLOW_THRESHOLD;
          following.current = bottom;
          setAtBottom(bottom);
        }}
        data-testid="chat-log"
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6">
          {messages.length === 0 && empty}
          {messages.map((message, index) =>
            message.role === "user" ? (
              <div
                key={index}
                className="ml-auto max-w-[85%] rounded-2xl bg-muted px-4 py-2.5 text-[15px] leading-relaxed break-words whitespace-pre-wrap md:text-sm"
                data-testid="chat-message-user"
              >
                {message.content}
              </div>
            ) : (
              <div key={index} className="group flex flex-col gap-1">
                {message.content && (
                  <div
                    className="text-[15px] leading-relaxed break-words md:text-sm"
                    data-testid="chat-message-assistant"
                  >
                    <Markdown components={MARKDOWN}>{message.content}</Markdown>
                  </div>
                )}
                {!(streaming && index === messages.length - 1) && message.content && (
                  <div className="-ml-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <CopyButton text={message.content} />
                  </div>
                )}
              </div>
            ),
          )}
          {waiting && (
            <div
              className="flex items-center gap-1"
              role="status"
              aria-label={t("assistant.thinking")}
            >
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className="size-2 animate-bounce rounded-full bg-muted-foreground/60"
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className={cn(
          "absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full shadow-md transition-opacity",
          atBottom ? "pointer-events-none opacity-0" : "opacity-100",
        )}
        onClick={() => {
          following.current = true;
          toBottom("smooth");
        }}
        aria-label={t("assistant.scrollDown")}
        tabIndex={atBottom ? -1 : 0}
      >
        <ArrowDown />
      </Button>
    </div>
  );
}
